import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import Badge from "@/components/foundation/Badge";
import Button from "@/components/foundation/Button";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import GridPattern from "@/components/foundation/GridPattern";

import NetworkVisualization from "@/components/marketing/NetworkVisualization";
import MarketplaceWaitlistForm from "@/components/marketing/MarketplaceWaitlistForm";
import { AutoLinkPartners } from "@/components/marketing/PartnerLink";
import {
  Search,
  Cpu,
  Gauge,
  LineChart,
  RefreshCw,
  Plug,
} from "lucide-react";

const OFFERINGS = [
  { icon: Search, title: "Merchant Discovery", body: "Filter verified merchants by ZenScore, tenure, industry, and cash flow shape." },
  { icon: Cpu, title: "AI Underwriting", body: "Structured banking data + Uptiq AI-driven insights on every deal file." },
  { icon: Gauge, title: "ZenScore Integration", body: "Native score-based filtering, thresholds, and category breakdowns." },
  { icon: LineChart, title: "Portfolio Analytics", body: "Volume, concentration, and cadence dashboards at the portfolio level." },
  { icon: RefreshCw, title: "Repayment Monitoring", body: "Live repayment status feeding back into merchant scoring loops." },
  { icon: Plug, title: "API Access", body: "Programmatic connectivity to deal flow, ZenScore data, and events." },
];

const PRODUCTS = [
  "Merchant Cash Advances",
  "Working Capital",
  "Equipment Financing",
  "Lines of Credit",
  "Invoice Factoring",
  "SBA Referral Network",
  "Business Banking",
  "Embedded Finance",
];

export default function Marketplace() {
  const navigate = useNavigate();

  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · Marketplace — Coming Soon"
        description="The future of business capital. ZenMe is building an intelligent marketplace where verified businesses and funding partners meet — powered by structured data and AI underwriting."
        path="/marketplace"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-24"
        data-testid="marketplace-hero"
      >
        <GridPattern variant="dots" size={28} opacity={0.4} />
        <GlowOrb color="emerald" size={620} intensity={0.24} className="-bottom-40 -left-40" />

        <Container className="relative z-10">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <FadeIn>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-brand-emerald">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald shadow-[0_0_10px_rgba(0,230,118,0.8)]" />
                  Marketplace · Coming Soon
                </span>
              </FadeIn>
              <FadeIn delay={0.06}>
                <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
                  The future of{" "}
                  <span className="text-brand-emerald">business capital.</span>
                </h1>
              </FadeIn>
              <FadeIn delay={0.14}>
                <p className="mt-6 max-w-xl text-base text-text-secondary sm:text-lg leading-relaxed">
                  ZenMe is building the intelligent marketplace where verified
                  businesses and funding partners meet — powered by structured
                  data, AI underwriting, and continuous repayment intelligence.
                </p>
              </FadeIn>

              <FadeIn delay={0.22} className="mt-8">
                <MarketplaceWaitlistForm
                  source="marketplace_hero"
                  data-testid="marketplace-hero-waitlist"
                />
              </FadeIn>
            </div>

            <div className="flex items-center justify-center lg:col-span-6 lg:justify-end">
              <FadeIn delay={0.2} direction="left">
                <NetworkVisualization />
              </FadeIn>
            </div>
          </div>
        </Container>
      </section>

      {/* WHAT THE MARKETPLACE OFFERS */}
      <Section
        eyebrow="What the Marketplace Will Offer"
        headline="One intelligent platform. Every workflow."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {OFFERINGS.map((o, i) => {
            const Icon = o.icon;
            return (
              <FadeIn key={o.title} delay={0.05 * i}>
                <Card variant="elevated" className="relative flex h-full flex-col p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald">
                      <Icon className="h-4 w-4" strokeWidth={1.75} />
                    </div>
                    <Badge variant="soft">Coming Soon</Badge>
                  </div>
                  <h4 className="mt-4 font-display text-base font-semibold text-white">
                    {o.title}
                  </h4>
                  <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                    <AutoLinkPartners text={o.body} />
                  </p>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* FUTURE PRODUCTS */}
      <Section
        eyebrow="Future Capital Products"
        headline="Every form of business capital."
        subheadline="Over time, ZenMe will support the full range of funding options a modern business might need — with the same intelligence layer underneath."
      >
        <ul className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.01]">
          {PRODUCTS.map((p, i) => (
            <FadeIn key={p} delay={0.03 * i}>
              <li className="group flex items-center justify-between border-b border-white/[0.04] px-6 py-5 last:border-b-0 transition-colors hover:bg-white/[0.02]">
                <div className="flex items-center gap-4">
                  <span className="h-px w-6 bg-brand-emerald/60 transition-all group-hover:w-10" />
                  <span className="text-base font-medium text-white">{p}</span>
                </div>
                <Badge variant="soft">Coming Soon</Badge>
              </li>
            </FadeIn>
          ))}
        </ul>
      </Section>

      {/* VISION STATEMENT */}
      <section className="relative overflow-hidden py-32 sm:py-40">
        <GlowOrb color="emerald" size={720} intensity={0.16} className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        <Container className="relative z-10">
          <div className="mx-auto max-w-4xl text-center">
            <FadeIn>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-brand-emerald">
                Our Vision
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
                One intelligent platform. Every form of business capital.{" "}
                <span className="text-brand-emerald">
                  Powered by data — not gut feel.
                </span>
              </h2>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* WAITLIST BAND */}
      <Section
        eyebrow="Marketplace Waitlist"
        headline="Get early access."
        subheadline="Businesses and funding partners are both welcome. We'll reach out as the marketplace opens."
      >
        <FadeIn>
          <Card variant="glass" interactive={false} className="mx-auto max-w-2xl p-8 sm:p-10">
            <MarketplaceWaitlistForm
              source="marketplace_waitlist"
              data-testid="marketplace-waitlist-form-main"
            />
          </Card>
        </FadeIn>
      </Section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden py-24">
        <Container>
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 text-center">
            <FadeIn>
              <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">
                Also interested in becoming an early funding partner?
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate("/partners")}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Explore Funding Partner
              </Button>
            </FadeIn>
          </div>
        </Container>
      </section>
    </PageLayout>
  );
}
